'use client';

import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { HOVER_PLAY_DELAY } from '@/app/constants';
import { useDevice } from '@/app/hooks';
import { cn } from '@/app/utils';
import { playMediaSafely } from '@/app/utils/client';

/** 视频组上下文：同组内 hover 一个时，其他（含 autoPlayWhenInCenter）默认暂停 */
const VideoGroupContext = createContext<{
  activeHoverId: string | null;
  setActiveHoverId: (id: string | null) => void;
} | null>(null);

export const VideoGroupProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value: { activeHoverId: string | null; setActiveHoverId: (id: string | null) => void };
}) => {
  return <VideoGroupContext.Provider value={value}>{children}</VideoGroupContext.Provider>;
};

export interface VideoGroupPlayerProps {
  id: string;
  uri?: string;
  coverUri?: string;
  muted?: boolean;
  /** 当元素进入屏幕中间区域时自动播放，离开时暂停 */
  autoPlayWhenInCenter?: boolean;
  /** 是否显示模糊背景图 */
  showMaskBg?: boolean;
}

export const VideoGroupPlayer = ({
  id,
  uri,
  coverUri,
  muted = true,
  autoPlayWhenInCenter = false,
  showMaskBg,
}: VideoGroupPlayerProps) => {
  const videoGroup = useContext(VideoGroupContext);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isInCenterRef = useRef(false);
  const isHoveringRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const { isMobile } = useDevice();

  const setRef = useCallback((el: HTMLDivElement | null) => {
    containerRef.current = el;
  }, []);

  // 屏幕中间区域自动播放
  useEffect(() => {
    if (!autoPlayWhenInCenter || !uri || isMobile) return;
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        const video = videoRef.current;
        if (!video) return;

        isInCenterRef.current = entry.isIntersecting;
        if (entry.isIntersecting) {
          playMediaSafely(video);
        } else if (!isHoveringRef.current) {
          video.pause();
        }
      },
      {
        root: null,
        rootMargin: '-25% 0px -25% 0px',
        threshold: 0.2,
      },
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [autoPlayWhenInCenter, uri, isMobile]);

  // 同组内 hover 其他视频时暂停当前；离开时若在中间则恢复
  const shouldPauseByOtherHover =
    videoGroup && autoPlayWhenInCenter && videoGroup.activeHoverId !== null && videoGroup.activeHoverId !== id;
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (shouldPauseByOtherHover) {
      video.pause();
    } else if (autoPlayWhenInCenter && isInCenterRef.current) {
      playMediaSafely(video);
    }
  }, [shouldPauseByOtherHover, autoPlayWhenInCenter]);

  const handleMouseEnter = () => {
    if (isMobile) return;
    isHoveringRef.current = true;
    videoGroup?.setActiveHoverId(id);
    timerRef.current = setTimeout(() => {
      const video = videoRef.current;
      playMediaSafely(video);
    }, HOVER_PLAY_DELAY);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    isHoveringRef.current = false;
    videoGroup?.setActiveHoverId(null);
    const video = videoRef.current;
    if (video && !isInCenterRef.current) video.pause();
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleMediaLoaded = () => setIsLoading(false);

  return (
    <div
      className="relative h-full w-full cursor-pointer overflow-hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={setRef}
    >
      {showMaskBg && coverUri && (
        <div className="absolute top-0 right-0 bottom-0 left-0 z-0 h-full w-full overflow-hidden">
          <img
            src={coverUri}
            alt="Video background blur"
            className="h-full w-full scale-x-125 scale-y-105 object-cover opacity-[0.32] blur-[20px]"
          />
        </div>
      )}
      {isLoading && <div className="absolute inset-0 z-[-1] animate-pulse bg-gray-300/20" />}
      {isMobile ? (
        <img
          src={coverUri}
          alt="AI generated video"
          loading="eager"
          className={cn('h-full w-full object-contain', isLoading && 'opacity-0')}
          crossOrigin="anonymous"
          onLoad={handleMediaLoaded}
        />
      ) : (
        <div
          className={cn(
            'relative h-full w-full',
            !isLoading && 'opacity-100',
            'overflow-hidden transition-opacity duration-300',
          )}
        >
          <video
            ref={videoRef}
            preload="none"
            className="h-full w-full scale-[1.05] object-contain"
            crossOrigin="anonymous"
            muted={muted}
            loop
            playsInline
            webkit-playsinline="true"
            x5-playsinline="true"
            src={uri}
            onLoadedData={handleMediaLoaded}
            poster={coverUri}
          />
        </div>
      )}
    </div>
  );
};
